import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import queryString from 'query-string';
import {useTranslation} from 'react-i18next';
import i18n from '../../../i18n';
import AppBreadcrumb from '../../Shared/AppBreadcrumb';
import AppPagination from '../../Shared/AppPagination';
import KpiCard from '../../Shared/KpiCard';
import {ShimmerTableRows} from '../../Shared/Shimmer';
import httpService from '../../../services/http.service';

const FILTER_KEYS = ['status', 'q', 'from', 'to', 'referrer', 'page'];

const formatDate = (value) => value
    ? new Date(value.replace(' ', 'T')).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'fr-FR', {dateStyle: 'short', timeStyle: 'short'})
    : '';

const usd = (value) => `$${Number(value || 0).toFixed(2)}`;

const StatusBadge = ({status}) => {
    const {t} = useTranslation();
    if (status === 'rewarded') return <span className="badge badge-success ml-0">{t("Récompensé")}</span>;
    if (status === 'rejected') return <span className="badge badge-secondary ml-0">{t("Rejeté")}</span>;
    return <span className="badge badge-warning ml-0">{t("En attente")}</span>;
};

const UserLink = ({username, blocked}) => {
    const {t} = useTranslation();
    if (!username) return <span className="text-muted small">{t("Compte supprimé")}</span>;
    return (
        <Link to={`/users/${encodeURIComponent(username)}`} className="text-nowrap">
            @{username}{blocked ? <span className="text-muted small"> · {t("bloqué")}</span> : null}
        </Link>
    );
};

/** Gain d'une affiliation : montant versé au parrain et au filleul (vide tant que non récompensée). */
const Reward = ({value}) => (value === null || value === undefined || Number(value) === 0)
    ? <span className="text-muted">–</span>
    : <span>{usd(value)}</span>;

/**
 * Suivi des parrainages (/referrals) : indicateurs globaux, meilleurs parrains, filtres dans l'URL
 * (statut, recherche, période, parrain ; jamais d'identifiant) et tableau paginé des affiliations.
 */
const ReferralList = ({history, location}) => {
    const {t} = useTranslation();
    const query = queryString.parse(location.search);
    const status = query.status || '';

    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState(query.q || '');

    const setFilters = (changes) => {
        const next = {...query, ...changes};
        if (!('page' in changes)) delete next.page;
        Object.keys(next).forEach(key => (next[key] === '' || next[key] == null) && delete next[key]);
        history.push(`/referrals?${queryString.stringify(next)}`);
    };

    useEffect(() => {
        httpService.get('/referrals/stats').then(({data}) => setStats(data?.data || null)).catch(() => setStats(null));
    }, []);

    useEffect(() => {
        setSearch(query.q || '');
        setFetching(true);
        setError(null);
        const params = {};
        FILTER_KEYS.forEach(key => query[key] && (params[key] = query[key]));
        httpService.get('/referrals', {params})
            .then(({data}) => setPage(data))
            .catch((e) => setError(e?.response?.data?.message || t("Erreur de chargement.")))
            .finally(() => setFetching(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const onPaginate = (direction) => {
        const current = parseInt(page?.current_page || 1, 10);
        setFilters({page: direction === 'previous' ? current - 1 : current + 1});
    };

    const rows = page?.data || [];
    const states = stats?.states || {};
    const unavailable = (stats && stats.available === false) || page?.available === false;
    const hasFilters = query.q || query.status || query.from || query.to || query.referrer;

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={t("Suivi des parrainages")} />

            {unavailable && <div className="alert alert-warning">{t("Le système de parrainage n'est pas encore déployé.")}</div>}

            <div className="row clearfix equal-height-row">
                <div className="col-lg-3 col-md-6">
                    <KpiCard label={t("Parrainages")} value={stats?.total ?? '–'} icon="fa-users" tone="blue" loading={!stats}
                             hint={t("Aujourd'hui : {{n}}", {n: stats?.today ?? 0})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard label={t("Récompensés")} value={states.rewarded ?? '–'} icon="fa-check" tone="green" loading={!stats}
                             progress={stats?.conversion_percent} hint={t("Conversion : {{p}} %", {p: stats?.conversion_percent ?? 0})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard label={t("En attente")} value={states.pending ?? '–'} icon="fa-hourglass-half" tone="orange" loading={!stats}
                             hint={t("Rejetés : {{n}}", {n: states.rejected ?? 0})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard label={t("Versé (USD)")} value={usd((stats?.paid_usd?.referrers || 0) + (stats?.paid_usd?.referred || 0))} icon="fa-usd" tone="purple" loading={!stats}
                             hint={t("Parrains {{a}} · filleuls {{b}}", {a: usd(stats?.paid_usd?.referrers), b: usd(stats?.paid_usd?.referred)})} />
                </div>
            </div>

            {(stats?.top_referrers?.length > 0 || stats?.reject_reasons?.length > 0) && (
                <div className="row clearfix">
                    <div className="col-lg-6">
                        <div className="card">
                            <div className="header"><h2>{t("Meilleurs parrains")}</h2></div>
                            <div className="body p-0">
                                <ul className="list-group list-group-flush">
                                    {(stats.top_referrers || []).map(item => (
                                        <li key={item.username} className="list-group-item d-flex align-items-center" style={{cursor: 'pointer'}}
                                            onClick={() => setFilters({referrer: item.username, status: 'rewarded'})}>
                                            <span className="flex-grow-1">@{item.username}</span>
                                            <span className="badge badge-success mr-3">{t("{{n}} filleuls", {n: item.count})}</span>
                                            <small className="text-muted">{usd(item.earned_usd)}</small>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card">
                            <div className="header"><h2>{t("Motifs de rejet")}</h2></div>
                            <div className="body p-0">
                                {stats.reject_reasons?.length ? (
                                    <ul className="list-group list-group-flush">
                                        {stats.reject_reasons.map(item => (
                                            <li key={item.reason} className="list-group-item d-flex align-items-center">
                                                <span className="flex-grow-1">{item.reason}</span>
                                                <span className="badge badge-secondary">{item.count}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p className="text-muted p-3 mb-0">{t("Aucun rejet.")}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="body">
                    <div className="row">
                        <div className="col-lg-4 col-md-12 mb-2">
                            <form onSubmit={(e) => { e.preventDefault(); setFilters({q: search.trim()}); }}>
                                <div className="input-group">
                                    <input className="form-control" value={search} onChange={(e) => setSearch(e.target.value)}
                                           placeholder={t("Rechercher : parrain, filleul ou code")} />
                                    <div className="input-group-append">
                                        <button className="btn btn-primary" type="submit"><i className="fa fa-search"></i></button>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="col-lg-3 col-md-4 mb-2">
                            <select className="form-control" value={status} onChange={(e) => setFilters({status: e.target.value})}>
                                <option value="">{t("Tous les statuts")}</option>
                                <option value="pending">{t("En attente")}</option>
                                <option value="rewarded">{t("Récompensé")}</option>
                                <option value="rejected">{t("Rejeté")}</option>
                            </select>
                        </div>
                        <div className="col-lg-5 col-md-8 mb-2 d-flex">
                            <input type="date" className="form-control mr-1" title={t("Du")} value={query.from || ''} onChange={(e) => setFilters({from: e.target.value})} />
                            <input type="date" className="form-control" title={t("Au")} value={query.to || ''} onChange={(e) => setFilters({to: e.target.value})} />
                        </div>
                    </div>
                    {query.referrer && <span className="badge badge-info mr-2">{t("Parrain")} : @{query.referrer}</span>}
                    {hasFilters && (
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => history.push('/referrals')}>
                            <i className="fa fa-times"></i>{' '}{t("Effacer les filtres")}
                        </button>
                    )}
                </div>
            </div>

            <div className="card">
                <div className="body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead>
                            <tr>
                                <th>{t("Date")}</th>
                                <th>{t("Parrain")}</th>
                                <th>{t("Filleul")}</th>
                                <th>{t("Code")}</th>
                                <th>{t("Statut")}</th>
                                <th className="text-right">{t("Gain parrain")}</th>
                                <th className="text-right">{t("Gain filleul")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {fetching ? (
                                <ShimmerTableRows rows={8} columns={7} />
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={7} className="text-center text-muted py-4">{t("Aucun parrainage ne correspond aux filtres.")}</td></tr>
                            ) : rows.map(row => (
                                <tr key={row.referral_id}>
                                    <td className="text-nowrap small">{formatDate(row.created_at)}</td>
                                    <td><UserLink username={row.referrer_username} blocked={row.referrer_blocked} /></td>
                                    <td><UserLink username={row.referred_username} blocked={row.referred_blocked} /></td>
                                    <td><code>{row.code_used}</code></td>
                                    <td>
                                        <StatusBadge status={row.status} />
                                        {row.status === 'rejected' && row.reject_reason && <div className="small text-muted">{row.reject_reason}</div>}
                                        {row.status === 'pending' && row.expires_at && <div className="small text-muted">{t("Expire le {{d}}", {d: formatDate(row.expires_at)})}</div>}
                                    </td>
                                    <td className="text-right"><Reward value={row.reward_referrer_usd} /></td>
                                    <td className="text-right"><Reward value={row.reward_referred_usd} /></td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    {page && page.last_page > 1 && (
                        <div className="mt-3">
                            <AppPagination currentPage={page.current_page} lastPage={page.last_page} totalItems={page.total}
                                           hasPrevious={page.current_page > 1} hasNext={page.current_page < page.last_page} onNavigate={onPaginate} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReferralList;
